/**
 * Auth API Functions
 *
 * API calls for authentication endpoints.
 */

import { api, setTokens, clearTokens } from './client';

// =============================================================================
// Sign-in method tracking
// =============================================================================

// How the CURRENT session was authenticated. Drives UI like hiding the
// change-password form for Google sessions (a Google user may have no
// password they know of — admin-created accounts carry a random one).
// Survives reloads alongside the tokens; cleared with them on logout.
const AUTH_METHOD_KEY = 'auth_method';

export type AuthMethod = 'password' | 'google';

export function getAuthMethod(): AuthMethod | null {
  const value = localStorage.getItem(AUTH_METHOD_KEY);
  return value === 'password' || value === 'google' ? value : null;
}

function setAuthMethod(method: AuthMethod): void {
  localStorage.setItem(AUTH_METHOD_KEY, method);
}

// =============================================================================
// Types
// =============================================================================

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'CREW' | 'PLATFORM_ADMIN';
export type PlatformRole = 'USER' | 'PLATFORM_ADMIN';
export type OrgRole = 'OWNER' | 'ADMIN' | 'CREW';
export type SubscriptionTier = 'FREE' | 'PRO' | 'ENTERPRISE';

export interface OrgMembership {
  orgId: string;
  orgSlug: string;
  orgName: string;
  orgLogo?: string | null;
  paymentTrackingEnabled?: boolean;
  subscriptionTier?: SubscriptionTier;
  /**
   * IANA zone every date and time in the app is displayed in while this org
   * is current (utils/dateFormatting.ts). Only GET /auth/me carries it; the
   * login responses' token-payload orgs do not, hence the default fallback.
   */
  timezone?: string;
  /**
   * Whether this org currently sends SMS notifications — computed
   * server-side from the org's kill switch AND its effective tier (season
   * pass counts). Never re-derive from subscriptionTier client-side.
   */
  smsEnabled?: boolean;
  role: OrgRole;
}

/** Per-category channel toggles. Missing = server-side default. */
export interface ChannelPrefs {
  email?: boolean;
  sms?: boolean;
  push?: boolean;
}

export type ReminderTiming = 'WEEK_BEFORE' | 'DAY_BEFORE' | 'NIGHT_BEFORE' | 'TWO_HOURS_BEFORE';
export type ReminderMethod = 'email' | 'sms' | 'push';

/** A personal event reminder — applies to every event the user is crewed on. */
export interface EventReminder {
  timing: ReminderTiming;
  method: ReminderMethod;
}

/**
 * Mirrors backend services/notificationPrefs.ts. The SMS toggles are mute
 * switches on top of the separate verified opt-in — never consent itself.
 * The server MERGES this object with what's stored, so a settings card may
 * send only the keys it owns.
 */
export interface NotificationPreferences {
  email?: boolean; // legacy global toggle, still honored by the server
  sms?: boolean; // legacy global toggle
  assignments?: ChannelPrefs;
  newEvents?: ChannelPrefs;
  eventChanges?: ChannelPrefs;
  broadcasts?: ChannelPrefs;
  /** Org-wide weekly digest email — email-only category. */
  weeklyDigest?: ChannelPrefs;
  /** Owner/admin operational alerts (crew not yet notified) — email-only. */
  adminAlerts?: ChannelPrefs;
  /** Up to 2 (backend services/reminder.service.ts MAX_REMINDERS). */
  reminders?: EventReminder[];
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: UserRole; // Optional - role is now determined per-org via orgs array
  platformRole?: PlatformRole;
  status?: string;
  profilePhoto?: string;
  notificationPreferences?: NotificationPreferences | null;
  orgs?: OrgMembership[];
  createdAt?: string;
  lastLoginAt?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface SetupAccountRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  notificationPreferences?: NotificationPreferences;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  organizationSlug: string;
  /** Must be true — the Terms/Privacy checkbox (recorded server-side). */
  acceptTerms: boolean;
  /** Cloudflare Turnstile response token (required when Turnstile is configured). */
  turnstileToken?: string;
}

export interface CheckSlugResponse {
  available: boolean;
  reason?: 'invalid_format' | 'reserved' | 'taken';
}

export interface SignupResponse {
  message: string;
  user: User;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  requiresVerification: boolean;
  accessToken?: string;
  refreshToken?: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Login with email and password
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/auth/login', data);

  // Store tokens
  setTokens(response.data.accessToken, response.data.refreshToken);
  setAuthMethod('password');

  return response.data;
}

/**
 * Login with a Google ID token (Google Identity Services credential)
 */
export async function loginWithGoogle(idToken: string): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/auth/google', { idToken });

  // Store tokens
  setTokens(response.data.accessToken, response.data.refreshToken);
  setAuthMethod('google');

  return response.data;
}

/**
 * Logout - clear tokens
 */
export function logout(): void {
  clearTokens();
  localStorage.removeItem(AUTH_METHOD_KEY);
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<{ user: User }> {
  const response = await api.get<{ user: User }>('/auth/me');
  return response.data;
}

/**
 * Update current user profile
 */
export async function updateProfile(
  data: UpdateProfileRequest
): Promise<{ message: string; user: User }> {
  const response = await api.put<{ message: string; user: User }>(
    '/auth/profile',
    data
  );
  return response.data;
}

/**
 * Change password
 */
export async function changePassword(
  data: ChangePasswordRequest
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(
    '/auth/change-password',
    data
  );
  return response.data;
}

/**
 * Request password reset email
 */
export async function forgotPassword(
  data: ForgotPasswordRequest
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(
    '/auth/forgot-password',
    data
  );
  return response.data;
}

/**
 * Reset password with token
 */
export async function resetPassword(
  data: ResetPasswordRequest
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(
    '/auth/reset-password',
    data
  );
  return response.data;
}

/**
 * Setup account (new user sets password)
 */
export async function setupAccount(
  data: SetupAccountRequest
): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/auth/setup-account', data);

  // Store tokens (user is automatically logged in)
  setTokens(response.data.accessToken, response.data.refreshToken);
  setAuthMethod('password');

  return response.data;
}

/**
 * Self-service signup - create user and organization
 */
export async function signup(data: SignupRequest): Promise<SignupResponse> {
  const response = await api.post<SignupResponse>('/auth/signup', data);

  // If no verification required, store tokens (user is automatically logged in)
  if (!response.data.requiresVerification && response.data.accessToken && response.data.refreshToken) {
    setTokens(response.data.accessToken, response.data.refreshToken);
    setAuthMethod('password');
  }

  return response.data;
}

export interface GoogleSignupRequest {
  idToken: string;
  organizationName: string;
  organizationSlug: string;
  acceptTerms: boolean;
  turnstileToken?: string;
}

/**
 * Google-based signup: user + organization from a verified Google ID token.
 * No password, no email verification — the response is an auto-login.
 */
export async function signupWithGoogle(data: GoogleSignupRequest): Promise<SignupResponse> {
  const response = await api.post<SignupResponse>('/auth/signup/google', data);
  if (response.data.accessToken && response.data.refreshToken) {
    setTokens(response.data.accessToken, response.data.refreshToken);
    setAuthMethod('google');
  }
  return response.data;
}

/**
 * Live org-URL availability probe for the signup form
 */
export async function checkSlug(slug: string): Promise<CheckSlugResponse> {
  const response = await api.get<CheckSlugResponse>('/auth/check-slug', {
    params: { slug },
  });
  return response.data;
}

/**
 * Verify email address with token
 */
export async function verifyEmail(data: VerifyEmailRequest): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/auth/verify-email', data);

  // Store tokens (user is automatically logged in after verification)
  setTokens(response.data.accessToken, response.data.refreshToken);
  setAuthMethod('password');

  return response.data;
}

/**
 * Resend email verification link
 */
export async function resendVerification(
  data: ResendVerificationRequest
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(
    '/auth/resend-verification',
    data
  );
  return response.data;
}
